// Kelola varian stok (khusus Owner; backend juga memvalidasi Owner). Hapus permanen hanya bila saldo 0 & tanpa riwayat.
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { notify } from "./modal_center";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/stock`;
const STD_SIZES = ["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
const SLEEVES = [["pendek", "Pendek"], ["panjang", "Panjang"], ["tidak_berlaku", "Tanpa lengan"]];
const errMsg = (e, d) => { const x = e?.response?.data?.detail; return typeof x === "string" ? x : x?.message || d; };

function Modal({ title, onClose, children, testid }) {
  return createPortal(<div className="svm-backdrop" onClick={onClose} data-testid={`${testid}-backdrop`}>
    <div className="svm-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()} data-testid={testid}>
      <div className="svm-head">
        <h3 className="svm-title">{title}</h3>
        <button type="button" className="stock-prod-close" onClick={onClose} aria-label="Tutup" data-testid={`${testid}-close`}>×</button>
      </div>
      <div className="svm-body">{children}</div>
    </div>
  </div>, document.body);
}

const ADD_SIZES = ["S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL"];
// XXL → 2XL, XXXL → 3XL, dst. (sama dengan pembacaan AI) agar ukuran tidak dobel.
export const canonSize = (s) => {
  const x = String(s || "").trim().toUpperCase().replace(/\s+/g, "");
  const m = x.match(/^(X{2,5})L$/);
  return m ? `${m[1].length}XL` : x;
};
export const missingSizes = (rows) => { const have = new Set(rows.map(v => canonSize(v.size))); return ADD_SIZES.filter(s => !have.has(s)); };

// Buat varian baru (stok 0 pcs + saldo awal 0 resmi). Varian lama tidak disentuh.
async function createSizes(pid, sleeve, color, sizes) {
  let ok = 0; const dup = [], bad = [], ids = [];
  for (const size of sizes) {
    try { const r = await axios.post(`${API}/variants`, { category: "polosan", product_id: pid, sleeve, color: color.trim(), size }); ok++; if (r.data?.id) ids.push(r.data.id); }
    catch (e) { (e?.response?.status === 409 ? dup : bad).push(`${size}${e?.response?.status === 409 ? "" : ` (${errMsg(e, "gagal")})`}`); }
  }
  if (ids.length) {
    try { await axios.post(`${API}/opening-balance`, { counted_at: new Date().toISOString(), note: "Saldo awal varian baru (0 pcs)", client_key: crypto.randomUUID(),
      lines: ids.map(variant_id => ({ variant_id, qty: 0 })) }); }
    catch (e) { toast.warning(`Varian dibuat, tetapi saldo awal belum terkonfirmasi: ${errMsg(e, "gagal")}`); }
  }
  if (ok) toast.success(`${ok} varian ditambahkan (saldo awal 0 pcs)`);
  if (dup.length) toast.warning(`Sudah ada, dilewati: ${dup.join(", ")}`);
  if (bad.length) toast.error(`Gagal: ${bad.join(", ")}`);
  return { ok, failed: dup.length + bad.length };
}

function AddSize({ pid, color, sleeve, rows, onClose, onDone }) {
  const [inactive, setInactive] = useState([]);
  const [revBusy, setRevBusy] = useState("");
  useEffect(() => {
    const have = new Set(rows.map(v => canonSize(v.size))), c = (color || "").trim().toLowerCase();
    axios.get(`${API}/variants`, { params: { product_id: pid, sleeve, include_inactive: true } })
      .then(r => setInactive((Array.isArray(r.data) ? r.data : r.data?.items || []).filter(v => v.is_active === false
        && (v.color || "").trim().toLowerCase() === c && !have.has(canonSize(v.size)))))
      .catch(() => setInactive([]));
  }, [pid, sleeve, color, rows]);
  const options = missingSizes(rows).filter(s => !inactive.some(v => canonSize(v.size) === s));
  // Aktifkan kembali varian lama (ID, SKU, warna, saldo & riwayat tetap — hanya is_active).
  const revive = async (v) => {
    setRevBusy(v.id);
    try {
      await axios.patch(`${API}/variants/${v.id}`, { is_active: true });
      toast.success(`Ukuran ${v.size} diaktifkan kembali`); onDone(); onClose();
    } catch (e) { toast.error(`Gagal mengaktifkan ${v.size}: ${errMsg(e, "gagal")}`); }
    finally { setRevBusy(""); }
  };
  const [sizes, setSizes] = useState([]), [busy, setBusy] = useState(false);
  const toggle = (s) => setSizes(x => x.includes(s) ? x.filter(y => y !== s) : [...x, s]);
  const save = async () => {
    setBusy(true);
    const r = await createSizes(pid, sleeve, color, sizes);
    setBusy(false);
    if (r.ok) onDone();
    if (!r.failed) onClose();
  };
  return <Modal title={`Tambah Ukuran — ${color} · ${sleeveLabel(sleeve)}`} onClose={onClose} testid="svm-addsize">
    <div className="svm-meta">Sudah ada: {rows.map(v => v.size).join(", ") || "—"}. Ukuran baru mulai 0 pcs; stok lama tidak berubah.</div>
    {options.length ? <div className="svm-chips">{options.map(s => <button key={s} type="button" className={`svm-chip${sizes.includes(s) ? " is-on" : ""}`}
      onClick={() => toggle(s)} data-testid={`svm-addsize-${s}`}>{s}</button>)}</div>
      : !inactive.length && <div className="svm-meta" data-testid="svm-addsize-full">Semua ukuran standar (S–5XL) sudah tersedia.</div>}
    {inactive.length > 0 && <div className="svm-meta" data-testid="svm-addsize-inactive">
      <div style={{margin:"8px 0 6px"}}>Pernah dibuat lalu dinonaktifkan — aktifkan kembali (ID, SKU, saldo, dan riwayat tetap):</div>
      <div className="svm-sizes">{inactive.map(v => <button key={v.id} type="button" className="svm-btn" disabled={!!revBusy}
        onClick={() => revive(v)} data-testid={`svm-addsize-revive-${canonSize(v.size)}`}>
        {revBusy === v.id ? "Mengaktifkan…" : `Aktifkan Kembali ${canonSize(v.size)}`}</button>)}</div>
    </div>}
    <button type="button" className="primary" disabled={busy || !sizes.length} onClick={save} data-testid="svm-addsize-save">
      {busy ? "Menyimpan…" : `Tambah ${sizes.length || ""} Ukuran`}</button>
  </Modal>;
}

function AddVariant({ product, onClose, onDone }) {
  const pid = product.rows[0]?.product_id;
  const sleeves0 = [...new Set(product.rows.map(v => v.sleeve).filter(Boolean))];
  const [color, setColor] = useState(""), [sleeve, setSleeve] = useState(sleeves0[0] || "pendek");
  const [sizes, setSizes] = useState([]), [busy, setBusy] = useState(false);
  const known = [...new Set([...product.rows.map(v => v.size), ...STD_SIZES])];
  const toggle = (s) => setSizes(x => x.includes(s) ? x.filter(y => y !== s) : [...x, s]);
  const save = async () => {
    setBusy(true);
    const r = await createSizes(pid, sleeve, color, sizes);
    setBusy(false);
    if (r.ok) onDone();
    if (!r.failed) onClose();
  };
  return <Modal title={`Tambah Varian — ${product.name}`} onClose={onClose} testid="svm-add">
    <label className="small">Warna
      <input list="svm-colors" value={color} onChange={e => setColor(e.target.value)} placeholder="mis. Hitam" data-testid="svm-add-color"/>
      <datalist id="svm-colors">{[...new Set(product.rows.map(v => v.color))].map(c => <option key={c} value={c}/>)}</datalist>
    </label>
    <label className="small">Lengan
      <select value={sleeve} onChange={e => setSleeve(e.target.value)} data-testid="svm-add-sleeve">
        {SLEEVES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
    </label>
    <div className="small">Ukuran (pilih beberapa)
      <div className="svm-chips">{known.map(s => <button key={s} type="button" className={`svm-chip${sizes.includes(s) ? " is-on" : ""}`}
        onClick={() => toggle(s)} data-testid={`svm-add-size-${s}`}>{s}</button>)}</div>
    </div>
    <button type="button" className="primary" disabled={busy || !color.trim() || !sizes.length} onClick={save} data-testid="svm-add-save">
      {busy ? "Menyimpan…" : `Tambah ${sizes.length || ""} Varian`}</button>
  </Modal>;
}

async function removeVariants(rows, label, onDone) {
  const ids = rows.map(v => v.id);
  let u;
  try { u = (await axios.post(`${API}/variants/usage`, { ids })).data.items; } catch (e) { toast.error(errMsg(e, "Gagal memeriksa varian")); return; }
  const used = u.filter(x => !x.can_delete);
  if (!used.length) {
    const ok = await notify.confirm({ title: "Hapus permanen?", message: `${label}: ${ids.length} varian (saldo 0, tanpa riwayat) akan dihapus permanen.`,
      primaryLabel: "Ya, hapus", secondaryLabel: "Batal", tone: "danger" });
    if (!ok) return;
    try { await axios.post(`${API}/variants/delete`, { ids }); toast.success(`${ids.length} varian dihapus`); onDone(); }
    catch (e) { toast.error(errMsg(e, "Gagal menghapus")); }
    return;
  }
  const can = u.filter(x => x.can_deactivate);
  const blocked = u.length - can.length;
  const ok = await notify.confirm({ title: "Tidak bisa dihapus permanen",
    message: `${label}: ${used.length} dari ${ids.length} varian sudah punya stok/riwayat. ` +
      (can.length ? `Nonaktifkan ${can.length} varian agar tidak tampil di daftar aktif? Data riwayat tetap tersimpan.` : "") +
      (blocked ? ` ${blocked} varian masih punya stok atau kebutuhan produksi aktif, tidak bisa dinonaktifkan.` : ""),
    primaryLabel: can.length ? `Nonaktifkan ${can.length}` : "Tutup", secondaryLabel: "Batal" });
  if (!ok || !can.length) return;
  let n = 0;
  for (const x of can) { try { await axios.patch(`${API}/variants/${x.id}`, { is_active: false }); n++; } catch (e) { toast.error(errMsg(e, "Gagal menonaktifkan")); } }
  if (n) { toast.success(`${n} varian dinonaktifkan`); onDone(); }
}

// Kelompok warna tidak membedakan huruf besar-kecil/spasi (Hitam = HITAM); label = tulisan yang paling banyak dipakai. Data varian tidak diubah.
export const colorKey = (c) => String(c || "").trim().replace(/\s+/g, " ").toLowerCase();
export const colorLabel = (rows) => {
  const n = {}; rows.forEach(v => { n[v.color] = (n[v.color] || 0) + 1; });
  return Object.keys(n).sort((a, b) => n[b] - n[a] || a.localeCompare(b))[0];
};
export const groupByColor = (rows) => {
  const g = {};
  rows.forEach(v => { const c = (g[colorKey(v.color)] = g[colorKey(v.color)] || { color: v.color, rows: [], sleeves: {} }); c.rows.push(v); (c.sleeves[v.sleeve || ""] = c.sleeves[v.sleeve || ""] || []).push(v); });
  return Object.values(g).map(x => ({ ...x, color: colorLabel(x.rows) }));
};
const sleeveLabel = (k) => k === "pendek" ? "Lengan Pendek" : k === "panjang" ? "Lengan Panjang" : k === "tidak_berlaku" ? "Tanpa Lengan" : "Lainnya";

function ColorGroup({ g, gi, open, onToggle, onRename, onDone, onAddSize }) {
  const total = g.rows.reduce((t, v) => t + Number(v.current_qty || 0), 0);
  return <div className={`svm-group${open ? " is-open" : ""}`} data-testid={`svm-group-${gi}`}>
    <button type="button" className="svm-group-toggle" onClick={onToggle} aria-expanded={open} data-testid={`svm-toggle-${gi}`}>
      <span className="svm-caret">{open ? "▾" : "▸"}</span><b className="svm-color-name">{g.color}</b>
      <span className="svm-meta">{g.rows.length} varian · {total} pcs</span>
    </button>
    {open && <div className="svm-group-body">
      <div className="svm-group-actions">
        <button type="button" className="svm-btn" onClick={onRename} data-testid={`svm-rename-${gi}`}>Ubah Nama</button>
        <button type="button" className="svm-btn svm-danger" onClick={() => removeVariants(g.rows, `Warna ${g.color}`, onDone)} data-testid={`svm-delete-color-${gi}`}>Hapus Warna</button>
      </div>
      {Object.entries(g.sleeves).map(([sk, rows]) => <div key={sk} className="svm-sleeve" data-testid={`svm-sleeve-${gi}-${sk || "x"}`}>
        <div className="svm-sleeve-title">{sleeveLabel(sk)} <span className="svm-meta">({rows.length})</span>
          {(sk === "pendek" || sk === "panjang") && <button type="button" className="svm-btn svm-addsize-btn" onClick={() => onAddSize(g, sk, rows)}
            data-testid={`svm-addsize-open-${gi}-${sk}`}>+ Tambah Ukuran</button>}</div>
        <div className="svm-sizes">{rows.map(v => <span key={v.id} className="svm-size" data-testid={`svm-size-${v.id}`}>
          <b>{v.size}</b><span className="svm-meta">{Number(v.current_qty || 0)} pcs</span>
          <button type="button" className="svm-x" aria-label={`Hapus ${v.size}`} onClick={() => removeVariants([v], `${g.color} ${sleeveLabel(sk)} ${v.size}`, onDone)}
            data-testid={`svm-delete-size-${v.id}`}>×</button></span>)}</div>
      </div>)}
    </div>}
  </div>;
}

function ManageVariants({ product, onClose, onDone }) {
  const groups = groupByColor(product.rows);
  const [openIdx, setOpenIdx] = useState(-1);
  const [adding, setAdding] = useState(null);
  const rename = async (g) => {
    const v0 = g.rows[0];
    const name = await notify.prompt({ title: "Ubah nama warna", message: `${g.color} — berlaku untuk semua lengan & ukuran warna ini di ${product.name}. ID, SKU, saldo, dan riwayat tidak berubah; nama lama tetap dikenali.`,
      placeholder: "Nama warna baru", required: true, primaryLabel: "Simpan", secondaryLabel: "Batal" });
    if (!name || !name.trim() || name.trim() === g.color) return;
    try {
      const r = await axios.post(`${API}/colors/rename`, { category: "polosan", product_name: v0.product_name, material: v0.material, old_color: g.color, new_color: name.trim() });
      toast.success(`${r.data?.variants_affected ?? ""} varian diganti ke ${name.trim()}`.trim()); onDone();
    } catch (e) { toast.error(`Tidak bisa diubah: ${errMsg(e, "gagal")}`); }
  };
  return <Modal title={`Kelola Varian — ${product.name}`} onClose={onClose} testid="svm-manage">
    <div className="svm-meta" data-testid="svm-summary">{groups.length} warna · {product.rows.length} varian. Ketuk warna untuk melihat ukuran.</div>
    {groups.map((g, gi) => <ColorGroup key={g.color} g={g} gi={gi} open={openIdx === gi}
      onToggle={() => setOpenIdx(openIdx === gi ? -1 : gi)} onRename={() => rename(g)} onDone={onDone}
      onAddSize={(grp, sk, rows) => setAdding({ color: grp.color, sleeve: sk, rows })}/>)}
    {adding && <AddSize pid={product.rows[0]?.product_id} color={adding.color} sleeve={adding.sleeve} rows={adding.rows}
      onClose={() => setAdding(null)} onDone={onDone}/>}
  </Modal>;
}

export function VariantManageButtons({ product, onChanged }) {
  const [open, setOpen] = useState("");
  if (!product.rows.length || !product.rows[0].product_id) return null;
  const done = () => onChanged && onChanged();
  return <div className="svm-actions" data-testid="svm-actions">
    <button type="button" className="primary small-btn" onClick={() => setOpen("add")} data-testid="svm-open-add">Tambah Varian</button>
    <button type="button" className="soft-btn small-btn" onClick={() => setOpen("manage")} data-testid="svm-open-manage">Kelola Varian</button>
    {open === "add" && <AddVariant product={product} onClose={() => setOpen("")} onDone={done}/>}
    {open === "manage" && <ManageVariants product={product} onClose={() => setOpen("")} onDone={() => { done(); setOpen(""); }}/>}
  </div>;
}

// Kelola Warna kain per kelompok (Nama Kain + Merek + Satuan): tambah, ubah nama (per varian, ID/SKU/saldo tetap), hapus/nonaktifkan.
function KainColorRow({ v, unit, onDone, setErr }) {
  const [name, setName] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const n = (name || "").trim();
    if (!n) { setErr("Nama warna wajib diisi."); return; }
    if (n === v.color) { setName(null); return; }
    setBusy(true); setErr("");
    try { await axios.patch(`${API}/variants/${v.id}`, { color: n }); toast.success(`Warna diganti ke ${n}`); onDone(); }
    catch (e) { setErr(`${v.color}: ${errMsg(e, "gagal menyimpan — periksa koneksi")}`); }
    finally { setBusy(false); }
  };
  if (name !== null) return <form className="svm-kc-row" onSubmit={e => { e.preventDefault(); if (!busy) save(); }} data-testid={`svm-kc-edit-${v.id}`}>
    <input className="svm-kc-input" value={name} onChange={e => setName(e.target.value)} autoFocus aria-label="Nama warna baru" data-testid={`svm-kc-name-${v.id}`}/>
    <div className="svm-kc-btns">
      <button type="button" className="svm-btn" onClick={() => setName(null)} disabled={busy} data-testid={`svm-kc-cancel-${v.id}`}>Batal</button>
      <button type="submit" className="svm-btn svm-primary" disabled={busy} data-testid={`svm-kc-save-${v.id}`}>{busy ? "Menyimpan…" : "Simpan"}</button>
    </div>
  </form>;
  return <div className="svm-kc-row" data-testid={`svm-kc-row-${v.id}`}>
    <span className="svm-kc-label"><b>{v.color || "—"}</b> <span className="svm-meta">{v.current_qty ?? 0} {unit}{v.is_active === false ? " · nonaktif" : ""}</span></span>
    <div className="svm-kc-btns">
      <button type="button" className="svm-btn" onClick={() => { setErr(""); setName(v.color || ""); }} data-testid={`svm-kc-rename-${v.id}`}>Ubah</button>
      <button type="button" className="svm-btn svm-danger" onClick={() => removeVariants([v], `Warna ${v.color}`, onDone)} data-testid={`svm-kc-delete-${v.id}`}>Hapus</button>
    </div>
  </div>;
}

function KainColorModal({ group, onClose, onDone }) {
  const [color, setColor] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const add = async () => {
    const c = color.trim();
    if (!c) { setErr("Nama warna wajib diisi."); return; }
    setBusy(true); setErr("");
    try {
      await axios.post(`${API}/variants`, { category: "kain", fabric_name: group.fabric_name, brand: group.brand || "", unit: group.unit, color: c });
      toast.success(`Warna ${c} ditambahkan (stok 0)`); onDone();
    } catch (e) { setErr(errMsg(e, "Gagal menambah warna — periksa koneksi")); }
    finally { setBusy(false); }
  };
  const title = [group.fabric_name, group.brand, `Satuan ${group.unit || "—"}`].filter(Boolean).join(" · ");
  return <Modal title={`Kelola Warna — ${title}`} onClose={onClose} testid="svm-kc">
    <form className="svm-kc-row svm-kc-add" onSubmit={e => { e.preventDefault(); if (!busy) add(); }}>
      <input className="svm-kc-input" value={color} onChange={e => setColor(e.target.value)} placeholder="Nama warna baru, mis. Hitam" aria-label="Nama warna baru" data-testid="svm-kc-add-color"/>
      <button type="submit" className="svm-btn svm-primary" disabled={busy} data-testid="svm-kc-add-save">{busy ? "Menyimpan…" : "Tambah Warna"}</button>
    </form>
    {err && <div className="vce-err" role="alert" data-testid="svm-kc-error">{err}</div>}
    <div className="svm-meta">{group.rows.length} warna. Hapus hanya untuk saldo 0 tanpa riwayat; selain itu ditawarkan Nonaktifkan. ID, SKU, saldo, dan riwayat tidak berubah saat ganti nama.</div>
    {group.rows.map(v => <KainColorRow key={v.id} v={v} unit={group.unit} onDone={onDone} setErr={setErr}/>)}
  </Modal>;
}

export function KainColorManageButton({ group, onChanged }) {
  const [open, setOpen] = useState(false);
  const tk = group.key.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
  return <>
    <button type="button" className="soft-btn small-btn" onClick={() => setOpen(true)} data-testid={`svm-kc-open-${tk}`}>Kelola Warna</button>
    {open && <KainColorModal group={group} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onChanged && onChanged(); }}/>}
  </>;
}
